import React, { useEffect, useState, useCallback } from "react";
import { HiOutlineUserAdd } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThemeContext, { themes } from "../../ThemeContext";
import "react-phone-number-input/style.css";

import Layout from "../Layout/Layout";
import DataSpinner from "../../CustomComponent/Spinner/Spinner";
import { NoTransactions } from "../../components/TableReusables/TableReusables";
import ToolTip from "../../CustomComponent/Tooltip/Tooltip";
import RenderMerchantLogo from "../../components/RendermerchantLogo/RenderMerchantLogo";
import PhoneInput from "react-phone-number-input";
import Select from "react-select";

import { handleUserCrudOperation } from "../../redux/actions/usersList";
import { RootState } from "../../redux/rootReducers";

import "./Merchant.scss";
import Link from "../../CustomComponent/Link/Link";
import ModalOnOff from "./merchantModalOnOff";

interface StatusOption {
  label: string;
  value: "ACTIVE" | "SUSPENDED";
}

const UsersByPartnerCards: React.FC = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.authReducer);
  const usersState = useSelector((state: RootState) => state?.userCrudReducer);
  const { t } = useTranslation("translation", { keyPrefix: "merchantScreen" });

  // States for create form
  const [showCreate, setShowCreate] = useState(false);
  const [partnerUser_name, setPartnerUser_name] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"ACTIVE" | "SUSPENDED">("ACTIVE");
  const [phone, setPhone] = useState<string | undefined>(undefined);

  // States for edit form
  const [showEdit, setShowEdit] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);

  const statusOptions: StatusOption[] = [
    { label: t("popUps.inputs.active")!, value: "ACTIVE" },
    { label: t("popUps.inputs.suspend")!, value: "SUSPENDED" },
  ];

  const normalizeStatus = (value?: string): "ACTIVE" | "SUSPENDED" | "" => {
    const normalized = value?.toString().trim().toUpperCase();
    if (normalized === "ACTIVE" || normalized === "SUSPENDED") return normalized;
    return "";
  };

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      height: 50,
      borderRadius: 10,
      overflow: "none",
      width: "100%",
      border: "1px solid rgba(207, 211, 212, 0.5019607843)",
      outline: "0px solid transparent",
      boxShadow: "none",
      padding: 0,
      margin: 0,
      fontSize: "12px",
      "&:focus-within": {
        boxShadow: "none",
        outline: "0px solid transparent",
        borderColor: `${themes.MainGreenColorRgba}`,
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: 0,
      margin: 0,
      paddingLeft: 10,
    }),
    input: (base: any) => ({
      ...base,
      padding: 0,
      margin: 0,
    }),
    placeholder: (defaultStyles: any) => {
      return {
        ...defaultStyles,
        color: "rgba(142, 149, 169, 1)",
      };
    },
  };

  const fetchUsers = useCallback(() => {
    dispatch(
      handleUserCrudOperation(
        "getByPartner",
        { pageNumber: 0, pageSize: 200, keyword: "" },
        auth.token,
        partnerId
      )
    );
  }, [dispatch, auth.token, partnerId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (usersState.request === "post" || usersState.request === "put") {
      fetchUsers();
      setShowCreate(false);
      setShowEdit(false);
    }
  }, [usersState.request]);

  const handleCreate = () => {
    if (!partnerUser_name || !email) return;

    dispatch(
      handleUserCrudOperation(
        "post",
        {
          partnerUser_name,
          email,
          phoneNumber: phone,
          role: "partner",
          status,
          partnerId,
        },
        auth.token,
        partnerId
      )
    );
  };

  // Handle edit user
  const handleEdit = () => {
    if (!editingUser) return;
    console.log("editingUser:::", editingUser);
    dispatch(
      handleUserCrudOperation(
        "put",
        {
          ...editingUser,
          partnerUser_name: editingUser.partnerUser_name,
          email: editingUser.email,
          phoneNumber: editingUser.phoneNumber,
          status: editingUser.status,
          partnerId: editingUser.partnerId,
        },
        auth.token,
        editingUser.userPartnerId
      )
    );
  };

  // Open edit modal with user data
  const openEditModal = (user: any) => {
    setEditingUser({ ...user, status: normalizeStatus(user.status) || user.status });
    setShowEdit(true);
  };

  // submit new user
  const handleSubmit = () => {
    if (!partnerUser_name || !email) return;
    dispatch(
      handleUserCrudOperation(
        "post",
        {
          partnerUser_name,
          email,
          phoneNumber: phone,
          role: "partner",
          status,
        },
        auth.token,
        partnerId
      )
    );
  };

  return (
    <Layout type="admin">
      <div id="merchant" className="content-side">
        <div className="merchant-container">
          {showCreate && (
            <div className="delete-conatiner">
              <ModalOnOff
                modalName="showCreate"
                rootEle="merchant"
                parentEle="delete-conatiner"
                childEle="edit-item"
              />
              <div className="edit-item">
                <div className="close-header">
                  <h5>{t("popUps.titles.addUser")}</h5>
                  <IoClose
                    color="var(--mint-green)"
                    size={25}
                    onClick={() => setShowCreate(false)}
                  />
                </div>
                <div className="input-container">
                  <div className="input-element">
                    <p>{t("popUps.inputs.PartnerUser_name")}</p>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("popUps.inputs.PartnerUser_name")}
                      value={partnerUser_name}
                      onChange={(e) => setPartnerUser_name(e.target.value)}
                    />
                  </div>
                  <div className="input-element">
                    <p>{t("popUps.inputs.PartnerUser_email")}</p>
                    <input
                      type="email"
                      className="form-control"
                      placeholder={t("popUps.inputs.PartnerUser_email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="input-element">
                    <p>{t("popUps.inputs.phone")}</p>
                    <PhoneInput
                      className="PhoneInput phone-space"
                      defaultCountry="SA"
                      placeholder={t("popUps.inputs.phoneplaceholder")}
                      value={phone}
                      onChange={setPhone}
                    />
                  </div>
                  <div className="input-element">
                    <p>{t("popUps.inputs.status")}</p>
                    <Select<StatusOption, false>
                      styles={customSelectStyles}
                      className="PhoneInput PhoneInput-select"
                      options={statusOptions}
                      value={
                        statusOptions.find((o) => o.value === status) ?? null
                      }
                      onChange={(opt) => {
                        if (opt) setStatus(opt.value);
                      }}
                      placeholder={t("popUps.inputs.statusplaceholder")}
                    />
                  </div>
                </div>
                <div className="btns-container">
                  <button
                    className="cancel-button"
                    onClick={() => setShowCreate(false)}
                  >
                    {t("popUps.buttons.cancel")}
                  </button>
                  <button
                    className="yes-btn"
                    onClick={handleCreate}
                    disabled={usersState?.loading}
                  >
                    {usersState?.loading ? (
                      <Spinner size="sm" />
                    ) : (
                      t("popUps.buttons.create")
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showEdit && editingUser && (
            <div className="delete-conatiner">
              <ModalOnOff
                modalName="showEdit"
                rootEle="merchant"
                parentEle="delete-conatiner"
                childEle="edit-item"
              />
              <div className="edit-item">
                <div className="close-header">
                  <h5>{t("popUps.titles.editUser")}</h5>
                  <IoClose
                    color="var(--mint-green)"
                    size={25}
                    onClick={() => setShowEdit(false)}
                  />
                </div>
                <div className="input-container">
                  <div className="input-element">
                    <p>{t("popUps.inputs.PartnerUser_name")}</p>
                    <input
                      type="text"
                      className="form-control"
                      value={editingUser.partnerUser_name || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          partnerUser_name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="input-element">
                    <p>{t("popUps.inputs.PartnerUser_email")}</p>
                    <input
                      type="email"
                      className="form-control"
                      value={editingUser.email}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="input-element">
                    <p>{t("popUps.inputs.phone")}</p>
                    <PhoneInput
                      className="PhoneInput phone-space"
                      defaultCountry="SA"
                      value={editingUser.phoneNumber}
                      onChange={(value) =>
                        setEditingUser({
                          ...editingUser,
                          phoneNumber: value,
                        })
                      }
                    />
                  </div>
                  <div className="input-element">
                    <p>{t("popUps.inputs.status")}</p>
                    <Select<StatusOption, false>
                      styles={customSelectStyles}
                      className="PhoneInput PhoneInput-select"
                      options={statusOptions}
                      value={
                        statusOptions.find(
                          (o) => o.value === normalizeStatus(editingUser.status)
                        ) ?? null
                      }
                      onChange={(opt) => {
                        if (opt)
                          setEditingUser({
                            ...editingUser,
                            status: opt.value,
                          });
                      }}
                    />
                  </div>
                </div>
                <div className="btns-container">
                  <button
                    className="cancel-button"
                    onClick={() => setShowEdit(false)}
                  >
                    {t("popUps.buttons.cancel")}
                  </button>
                  <button
                    className="yes-btn"
                    onClick={handleEdit}
                    disabled={usersState?.loading}
                  >
                    {usersState?.loading ? (
                      <Spinner size="sm" />
                    ) : (
                      t("popUps.buttons.save")
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* -- Header & Add Button -- */}
          <div className="header">
            <p className="header-name">{t("mainPage.usersList")}</p>
            <div className="header-button">
              <button
                className="button-primary-light"
                onClick={() => setShowCreate(true)}
              >
                <span className="icon">
                  <HiOutlineUserAdd />
                </span>
                <span className="text">{t("mainPage.addUser")}</span>
              </button>
            </div>
          </div>

          {/* -- Users Grid -- */}
          <div className="merchants-Wrappers">
            {usersState.loading ? (
              <DataSpinner />
            ) : usersState.error ? (
              <div>Error: {usersState.error}</div>
            ) : Array.isArray(usersState.users) &&
              usersState.users.length > 0 ? (
              usersState.users.map((user: any) => (
                <div
                  className="merchant-Card"
                  style={{ pointerEvents: "none" }}
                  key={user.keycloakUserId}
                >
                  <div className="circle-container">
                    <RenderMerchantLogo
                      logo={user?.logo}
                      merchantName={`${user?.partnerUser_name}`}
                    />
                    <div className="status">
                      <p
                        className={`circle ${
                          normalizeStatus(user.status) === "ACTIVE"
                            ? "active"
                            : "suspend"
                        }`}
                      />
                    </div>
                  </div>
                  <div className="wrapper-content">
                    <div className="info">
                      <h6 className="name">
                        {user?.partnerUser_name ? user?.partnerUser_name : "-"}
                      </h6>
                      <p className="email">{user.email}</p>
                      <p className="phone">{user.phoneNumber}</p>
                    </div>
                    <div className="card-end d-flex justify-content-center">
                      <button
                        className="button"
                        onClick={() => openEditModal(user)}
                        style={{ pointerEvents: "auto" }}
                      >
                        <ToolTip title={t("popUps.titles.editUser")}>
                          <MdEdit />
                        </ToolTip>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div>No users found</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UsersByPartnerCards;
